
import { Icon } from "@iconify/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/card";
import { StatusTag } from "@/shared/components/StatusTag";
import { Button } from "@/shared/components/button";
import type { HealthRecord } from "@/shared/types";
import { cn } from "@/shared/lib/utils";
import { Tooltip } from "@/shared/components/misc";
import { PageBackButton } from "@/shared/components/PageBackButton";

interface RecordDetailsProps {
  record: HealthRecord | null;
  onBack: () => void;
}

const statusConfig = {
  normal: { icon: "lucide:check-circle-2", label: "Normal" },
  low: { icon: "lucide:alert-triangle", label: "Low" },
  high: { icon: "lucide:alert-circle", label: "High" },
  critical: { icon: "lucide:alert-circle", label: "Critical" },
};

export function RecordDetails({ record, onBack }: RecordDetailsProps) {
  if (!record) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="text-muted-foreground">No record selected</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <PageBackButton onClick={onBack} ariaLabel="Back to records" className="!mt-1" />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-foreground">
                {record.title}
              </h1>
              <StatusTag
                status={record.status}
                label={record.status === "normal" ? "Normal" : "Needs Review"}
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Icon icon="lucide:calendar" className="h-4 w-4" />
                {new Date(record.date).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              <span className="flex items-center gap-1.5">
                <Icon icon="lucide:user" className="h-4 w-4" />
                {record.provider}
              </span>
              <span className="flex items-center gap-1.5">
                <Icon icon="lucide:building" className="h-4 w-4" />
                {record.facility}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Icon icon="lucide:download" className="mr-2 h-4 w-4" />
            Download
          </Button>
          <Button variant="outline" size="sm">
            <Icon icon="lucide:share-2" className="mr-2 h-4 w-4" />
            Share
          </Button>
          <Button variant="outline" size="sm">
            <Icon icon="lucide:printer" className="mr-2 h-4 w-4" />
            Print
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Plain Language Summary */}
        <Card className="border-primary/20 bg-primary/5 lg:col-span-3">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15">
                <Icon icon="lucide:lightbulb" className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  What This Means for You
                </h2>
                <p className="mt-2 text-foreground/90 leading-relaxed">
                  {record.plainLanguageSummary}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Results */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-semibold text-foreground">
            Detailed Results
          </h2>
          <div className="space-y-3">
            {record.details.map((detail, index) => {
              const config = statusConfig[detail.status];
              return (
                <Card key={index} className={cn("border", detail.status === "critical" ? "border-destructive" : "border-border")}>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                            detail.status === "critical"
                              ? "border border-destructive bg-destructive/5"
                              : "bg-primary/10"
                          )}
                        >
                          <Icon icon={config.icon} className={cn("h-5 w-5", detail.status === "critical" ? "text-destructive" : "text-primary")} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground">
                              {detail.label}
                            </p>
                            <Tooltip title={<p>{detail.explanation}</p>} placement="top" overlayClassName="max-w-xs">
                              <Icon icon="lucide:info" className="h-4 w-4 text-muted-foreground" />
                            </Tooltip>
                          </div>
                          <div className="mt-1 flex items-baseline gap-2">
                            <span className="text-2xl font-semibold text-foreground">
                              {detail.value}
                            </span>
                            {detail.unit && (
                              <span className="text-sm text-muted-foreground">
                                {detail.unit}
                              </span>
                            )}
                          </div>
                          {detail.normalRange && (
                            <p className="mt-1 text-sm text-muted-foreground">
                              Normal range: {detail.normalRange}
                            </p>
                          )}
                        </div>
                      </div>
                      <StatusTag status={detail.status} label={config.label} className="shrink-0" />
                    </div>
                    <div className="mt-4 rounded-lg bg-secondary/50 p-3">
                      <p className="text-sm text-foreground/90">
                        {detail.explanation}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Quick Info */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                Record Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                  <Icon icon="lucide:file-text" className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Record Type</p>
                  <p className="text-sm font-medium text-foreground capitalize">
                    {record.type.replace("_", " ")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                  <Icon icon="lucide:calendar" className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm font-medium text-foreground">
                    {new Date(record.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                  <Icon icon="lucide:user" className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Provider</p>
                  <p className="text-sm font-medium text-foreground">
                    {record.provider}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                  <Icon icon="lucide:building" className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Facility</p>
                  <p className="text-sm font-medium text-foreground">
                    {record.facility}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-secondary/30">
            <CardContent className="p-4">
              <h3 className="font-medium text-foreground">Have Questions?</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Use our &quot;Ask About Records&quot; feature to get answers about this
                result in plain language.
              </p>
              <Button variant="outline" size="sm" className="mt-3 w-full">
                Ask a Question
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
